import * as React from 'react';
import {
    AgendaViewPageProps,
    AgendaViewPageStyleProps,
    AgendaViewPageStyles,
} from './AgendaViewPage.types';
import { AgendaViewEvent } from '../AgendaViewEvent';
import { CalendarPrintPage } from '../CalendarPrintPage';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { DayOfWeek } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import { createRequestQueue, RequestQueue } from 'owa-service-utils';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { getVisibleDateRange } from '../CalendarPrintPage/getVisibleDateRange';
import { compare, formatWeekDayMonthDayYear } from 'owa-datetime';
import { agendaViewPageLoadingInfo } from './AgendaViewPage.base.locstring.json';
import loc, { format } from 'owa-localize';
import { returnEventsInDateRange, splitEventsIntoDays } from './eventFilters';

const getClassNames = classNamesFunction<AgendaViewPageStyleProps, AgendaViewPageStyles>();

const MAXIMUM_NUM_REQUESTS = 6;
let requestQueue: RequestQueue = null;

/** The base non-styled version of AgendaViewPage. */
export function AgendaViewPageBase<TEvent>(props: AgendaViewPageProps<TEvent>) {
    const { styles, className, preview, miniMonth, getDetailedEvent } = props;
    const classNames = getClassNames(styles, { className: className, preview: preview! });

    const { title, calendarColorScheme, calendarView, events } = props;

    const visibleDateRange = getVisibleDateRange(calendarView);
    const castedEvents: CalendarEvent[] = (events as unknown) as CalendarEvent[];

    // get the array of all items that are going to be in range. This contains every item exactly once, even multiday items,
    // so we don't query server for full details of items multiple times
    const eventsInRange = returnEventsInDateRange(
        visibleDateRange.start,
        visibleDateRange.end,
        castedEvents
    );

    // now build array of items per day. Multi-day items will then appear in every day they occur in
    // dates = visible dates in the range. Used for day headers
    // visibleEventsPerDay = 2D array of [date][events]
    let workweek =
        calendarView.workWeekDays && calendarView.workWeekDays.length > 0
            ? calendarView.workWeekDays
            : [DayOfWeek.Monday]; // if no work week defined, just start on Monday
    const { events: visibleEventsPerDay, dates } = splitEventsIntoDays(
        castedEvents,
        visibleDateRange.start,
        visibleDateRange.end,
        calendarView.view,
        workweek
    );

    const [isDataLoaded, setDataLoaded] = React.useState<boolean>(false);
    const [loadedEventsCount, setLoadedEventsCount] = React.useState<number>(0);
    const [data, setData] = React.useState<CalendarEvent[]>([]);
    React.useEffect(() => {
        loadData(setDataLoaded, setData, setLoadedEventsCount, eventsInRange, getDetailedEvent);
    }, [events, calendarView.view]);

    let sortedData =
        !data || data.length === 0
            ? []
            : data.sort((e1, e2) => {
                  // sort again by start time, data might come back out of order
                  return compare(e1.Start, e2.Start);
              });

    return (
        <CalendarPrintPage
            preview={preview}
            miniMonth={miniMonth}
            contentCanGrow={true}
            className={classNames.root}
            title={title}
            calendarColorScheme={calendarColorScheme}
            calendarView={calendarView}>
            <div className={classNames.eventsContainer}>
                <div className={classNames.inputBlocker} />
                {!isDataLoaded && (
                    <div>
                        {format(
                            loc(agendaViewPageLoadingInfo),
                            loadedEventsCount,
                            eventsInRange.length
                        )}
                    </div>
                )}
                {dates.map((date, i) => {
                    let ret = [];
                    if (visibleEventsPerDay[i].length > 0) {
                        ret.push(
                            <div className={classNames.dateHeader} key={i + 'dateHeader'}>
                                {formatWeekDayMonthDayYear(date)}
                            </div>
                        );
                    }
                    ret.push(
                        ...visibleEventsPerDay[i].map((partialEvent, j) => {
                            const fullEvent = sortedData.filter(
                                event => event.ItemId.Id === partialEvent.ItemId.Id
                            )[0];
                            return <AgendaViewEvent event={fullEvent} key={j} />;
                        })
                    );
                    return ret;
                })}
            </div>
        </CalendarPrintPage>
    );
}

async function loadData(
    setDataLoaded: (isLoaded: boolean) => void,
    setData: (data: CalendarEvent[]) => void,
    setLoadedEventsCount: (count: number) => void,
    events: CalendarEvent[],
    getDetailedEvent?: Function
) {
    setDataLoaded(false);
    let fullEvents = await getFullCalendarEvents(events, setLoadedEventsCount, getDetailedEvent);
    setData(fullEvents);
    setDataLoaded(true);
}

/**
 * Fetches all full calendar events from the server. Not using the cache because we only want these details for print,
 * not to get loaded and take up memory. Batches the requests to not get throttled
 * @param partialEvents
 */
async function getFullCalendarEvents(
    partialEvents: CalendarEvent[],
    setLoadedEventsCount: (newCount: number) => void,
    getDetailedEvent?: Function
): Promise<CalendarEvent[]> {
    if (!requestQueue) {
        requestQueue = createRequestQueue(MAXIMUM_NUM_REQUESTS);
    }

    setLoadedEventsCount(0);
    let events = [];
    let loadedEvents = 0;
    let promises = partialEvents.map(partialEvent => {
        return requestQueue.add(() => {
            return new Promise<void>(async resolve => {
                try {
                    let event = await getDetailedEvent(partialEvent);
                    loadedEvents++;
                    setLoadedEventsCount(loadedEvents);
                    if (event) {
                        events.push(event);
                    } else {
                        // if the GetCalendarEvent succeeds but has no data, still load the partial data for it
                        events.push(partialEvent);
                    }
                    resolve();
                } catch {
                    // if the GetCalendarEvent fails, still load the partial data for it
                    loadedEvents++;
                    setLoadedEventsCount(loadedEvents);
                    events.push(partialEvent);
                    resolve();
                }
            });
        });
    });

    // wait until all events come back
    await Promise.all(promises);

    return events;
}
